import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Filter, 
  Star, 
  Calendar,
  TrendingUp
} from 'lucide-react';

import { API_BASE_URL, API_KEY } from '../config/api.ts';
import FormSelect from '../components/FormSelect.tsx';

interface SurveyResult {
  _id: string;
  type: 'hotel_service' | 'app_feedback';
  branchId: {
    _id: string;
    name: string;
  };
  anonymizedResponses: {
    overallRating?: number;
    cleanlinessRating?: number;
    staffRating?: number;
    foodRating?: number;
    comfortRating?: number;
    valueRating?: number;
    recommendation?: boolean;
    feedback?: string;
    appRating?: number;
    easeOfUse?: number;
    featureRating?: number;
    appFeedback?: string;
    submittedAt: string;
  };
  completedAt: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalSurveys: number;
  limit: number;
}

interface AdminUser {
  userId: string;
  branchId: string;
  role: string;
}

const AnketSonuclari: React.FC = () => {
  const [surveys, setSurveys] = useState<SurveyResult[]>([]);
  const [allSurveys, setAllSurveys] = useState<SurveyResult[]>([]); // Tüm anketler (filtresiz) - ortalama hesaplaması için
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    rating: '', // Yeni: Yıldız filtresi
    page: 1,
    limit: 20
  });

  const [adminInfo, setAdminInfo] = useState<AdminUser | null>(null);
  const [branchName, setBranchName] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchAdminInfo();
  }, []);

  useEffect(() => {
    // useEffect triggered
    
    if (adminInfo) {
      fetchSurveyResults();
      // Tüm anketleri de çek (filtresiz) - ortalama hesaplaması için
      fetchAllSurveys();
    }
  }, [filters, adminInfo]);

  const fetchAdminInfo = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        setError('Admin token bulunamadı');
        return;
      }
      
      // Admin bilgilerini getir
      const response = await fetch(`${API_BASE_URL}/api/staff/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': API_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // API yanıtının yapısına göre adminData'yı al
        const adminData = data.data || data.staff;
        
        // Önce adminData'nın varlığını kontrol et
        if (!adminData) {
          setError('Admin bilgileri boş');
          return;
        }
        
        
        setAdminInfo(adminData);
        
        // Şube adını getir - branch objesinden branchId'yi al
        if (adminData.branch && adminData.branch._id) {
          // AdminInfo'ya branchId'yi ekle
          const adminWithBranchId = {
            ...adminData,
            branchId: adminData.branch._id
          };
          setAdminInfo(adminWithBranchId);
          fetchBranchName(adminData.branch._id);
        } else if (adminData.branchId) {
          // Eğer doğrudan branchId varsa
          fetchBranchName(adminData.branchId);
        } else {
          setError('Şube bilgisi bulunamadı');
        }
      } else {
        const errorData = await response.json();
        setError(`Admin bilgileri alınamadı: ${errorData.message || 'Bilinmeyen hata'}`);
      }
    } catch (error) {
      setError('Admin bilgileri getirilemedi');
    }
  };

  const fetchBranchName = async (branchId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE_URL}/api/branches/${branchId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': API_KEY
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBranchName(data.data.name);
      } else {
        const errorData = await response.json();
      }
    } catch (error) {
      // Şube adı getirilemedi
    }
  };

  const fetchSurveyResults = async () => {
    if (!adminInfo?.branchId) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      
      
      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: filters.limit.toString()
      });
      
      // Sadece otel hizmeti anketlerini getir ve rating filtresi ekle
      queryParams.append('type', 'hotel_service');
      if (filters.rating) queryParams.append('rating', filters.rating);

      const url = `${API_BASE_URL}/api/surveys/admin/results?${queryParams}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // API response yapısını kontrol et
        if (data.success && data.data) {
          setSurveys(data.data.surveys || []);
          setPagination(data.data.pagination || null);
          
        } else {
          setError('API yanıt formatı beklenmedik');
        }
      } else {
        try {
          const errorData = await response.json();
          setError(`Anket sonuçları alınamadı: ${errorData.message || 'Bilinmeyen hata'}`);
        } catch (parseError) {
          setError(`Anket sonuçları alınamadı: HTTP ${response.status}`);
        }
      }
    } catch (error) {
      setError(`Anket sonuçları getirilirken hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    } finally {
      setLoading(false);
    }
  };

  // Tüm anketleri çek (filtresiz) - ortalama hesaplaması için
  const fetchAllSurveys = async () => {
    if (!adminInfo?.branchId) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      // Tüm anketleri çek (filtresiz, sayfalama olmadan)
      const queryParams = new URLSearchParams({
        page: '1',
        limit: '1000' // Yeterince büyük bir sayı
      });
      
      queryParams.append('type', 'hotel_service');
      // Rating filtresi YOK - tüm anketleri al

      const url = `${API_BASE_URL}/api/surveys/admin/results?${queryParams}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data) {
          setAllSurveys(data.data.surveys || []);
        }
      }
    } catch (error) {
      // Sessizce hata yut - ortalama hesaplaması için kritik değil
      console.error('Tüm anketler alınamadı:', error);
    }
  };


  const getSurveyTypeLabel = (type: string) => {
    return type === 'hotel_service' ? 'Otel Hizmeti' : 'Uygulama Feedback';
  };

  const getSurveyTypeIcon = (type: string) => {
    return type === 'hotel_service' ? <Calendar size={16} /> : <ClipboardList size={16} />;
  };

  const renderRatingStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={`${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating})</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Anket sonuçları yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <p className="text-gray-700 font-semibold mb-4">{error}</p>
          <button 
            onClick={() => {
              setError('');
              fetchAdminInfo();
            }}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all font-bold shadow-lg"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  if (!adminInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <p className="text-gray-700 font-semibold">Admin bilgileri yüklenemedi</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">⭐ Otel Değerlendirmeleri</h1>
            <p className="text-gray-600 mt-2 font-medium">Müşteri puanları ve yorumlarını inceleyin</p>
          </div>
        </div>
      </div>

      {/* Branch Info */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center mr-3">
            <Calendar className="text-white" size={20} />
          </div>
          <div>
            <p className="text-green-900 font-bold">
              {branchName ? `${branchName} Şubesi` : 'Şube Bilgisi'}
            </p>
            <p className="text-green-700 text-sm font-medium">
              Sadece bu şubeye ait anket sonuçları gösteriliyor
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-gray-200">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center mr-3">
            <Filter size={18} className="text-green-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Filtreler</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Yıldız Filtresi"
            value={filters.rating}
            onChange={(e) => handleFilterChange('rating', e.target.value)}
          >
            <option value="">Tüm Yıldızlar</option>
            <option value="5">⭐⭐⭐⭐⭐ (5 Yıldız)</option>
            <option value="4">⭐⭐⭐⭐ (4 Yıldız)</option>
            <option value="3">⭐⭐⭐ (3 Yıldız)</option>
            <option value="2">⭐⭐ (2 Yıldız)</option>
            <option value="1">⭐ (1 Yıldız)</option>
          </FormSelect>

          <FormSelect
            label="Sayfa Başına"
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', e.target.value)}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </FormSelect>
        </div>
      </div>

      {/* Statistics */}
      {pagination && surveys.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl shadow-sm border-2 border-gray-200 hover:border-green-200 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Toplam Değerlendirme</p>
                <p className="text-3xl font-bold text-gray-900">{pagination.totalSurveys}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                <ClipboardList className="text-green-500" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-5 rounded-xl shadow-sm border-2 border-gray-200 hover:border-green-200 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600">Ortalama Otel Puanı</p>
                <div className="flex items-center">
                  <p className="text-3xl font-bold text-gray-900">
                    {allSurveys.length > 0 
                      ? (allSurveys.reduce((sum, s) => sum + (s.anonymizedResponses?.overallRating || 0), 0) / allSurveys.length).toFixed(1)
                      : '0.0'}
                  </p>
                  <span className="ml-2 text-lg text-gray-600">/5</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center">
                <Star className="text-green-500" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Survey Results */}
      <div className="bg-white rounded-xl shadow-md border-2 border-gray-200">
        <div className="px-6 py-5 bg-white border-b-2 border-gray-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center mr-3">
              <ClipboardList className="text-white" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                🏨 {branchName ? `${branchName} Oteli` : 'Oteliniz'} Müşteri Değerlendirmeleri
              </h3>
              <p className="text-sm text-gray-600 font-medium">
                Müşterilerinizin anonim puanları ve yorumları
              </p>
            </div>
          </div>
        </div>

        {surveys.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="text-green-500" size={32} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Henüz müşteri değerlendirmesi bulunmuyor</h3>
            <p className="text-gray-600">
              Bu otelde henüz tamamlanmış müşteri puanlaması bulunmuyor.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {surveys.map((survey) => (
              <div key={survey._id} className="p-6 hover:bg-gray-50 transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Ana Puan ve Tarih */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-2 mr-4">
                          {renderRatingStars(survey.anonymizedResponses?.overallRating || 0)}
                        </div>
                        <span className="text-3xl font-bold text-gray-900">
                          {survey.anonymizedResponses?.overallRating || 0}/5
                        </span>
                        <span className="ml-2 text-sm text-gray-600 font-medium">yıldız</span>
                      </div>
                      <span className="text-sm text-gray-500 font-medium bg-gray-50 px-3 py-1.5 rounded-lg">
                        {formatDate(survey.completedAt)}
                      </span>
                    </div>

                    {/* Müşteri Yorumu */}
                    {survey.anonymizedResponses?.feedback ? (
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-5">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                            <ClipboardList className="text-white" size={16} />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-900 mb-2">Müşteri Yorumu:</p>
                            <p className="text-gray-700 leading-relaxed italic font-medium">
                              "{survey.anonymizedResponses.feedback}"
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                        <p className="text-gray-500 text-sm italic font-medium">
                          💭 Müşteri yorum yazmamış
                        </p>
                      </div>
                    )}

                    {/* Anonim Bilgi */}
                    <div className="mt-4 flex items-center text-xs">
                      <span className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg font-semibold border border-green-200">
                        🔒 Anonim müşteri değerlendirmesi
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-6 py-4 rounded-xl shadow-sm border-2 border-gray-200">
          <div className="text-sm text-gray-700 font-semibold bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
            <span className="text-gray-900">
              {((pagination.currentPage - 1) * pagination.limit) + 1}
            </span>
            {' - '}
            <span className="text-gray-900">
              {Math.min(pagination.currentPage * pagination.limit, pagination.totalSurveys)}
            </span>
            {' / '}
            <span className="text-gray-900">{pagination.totalSurveys}</span>
            {' sonuç'}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Önceki
            </button>
            
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${
                  page === pagination.currentPage
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md'
                    : 'text-gray-600 bg-white border-2 border-gray-300 hover:bg-green-50 hover:border-green-300'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-green-50 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Sonraki
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AnketSonuclari;
